import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import { Box, FormControlLabel, Grid, Paper, Stack, Theme } from '@mui/material'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import SwitchComponent from '@professocial/professocialFormComponents/FormComponents/SwitchComponent'
import { AboType, Topic, TopicsWithMetadataResponse, TopicWithMetadata } from '@3m5/sm_publish-frontend-api'
import TopicMediaUpload from './TopicMediaUpload'
import DropDownComponent, {
  DropDownElement,
} from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import Preloader from '@professocial/professocialFormComponents/FormComponents/Preloader'
import { UploadTemporaryMediaFileRequest } from '@3m5/sm_publish-frontend-api'
import {
  uploadTemporaryMediaFileInitiated,
} from '../../../store/topic/topic.actions'
import { useDispatch, useSelector } from 'react-redux'
import TextFieldComponent from '@professocial/professocialFormComponents/FormComponents/TextFieldComponent'
import { getUploadStatus } from '../../../store/topic/topic.selector'
import { DisplayMode } from '@professocial/professocialTypes/types'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import { useHistory, useParams } from 'react-router'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(3),
    padding: theme.spacing(5),
  },
  button: {
    '& button': {
      minWidth: '250px',
    },
  },
  imgBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  labelInput: {
    minWidth: '100%',
    marginTop: theme.spacing(2),
  },
  descriptionInput: {
    width: '100%',
    '& .MuiInputBase-input': {
      height: 'auto !important',
    },
  },
}))

interface ITopicEditProps {
  aboTopics?: TopicsWithMetadataResponse
  initialValueDropdown: boolean
  newTopic?: boolean
  backToOverview?: () => void
  integrateImages?: boolean
  setShowTopicPopUp: React.Dispatch<React.SetStateAction<boolean>>
  showTopicPopUp: boolean
  editTopic?: (data: any) => void
  createTopic?: (data: any) => void
  subscribeAll?: (topicId: string, data: any) => void
  editMode: DisplayMode
}

const TopicEdit: React.FC<ITopicEditProps> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const history = useHistory()
  const { id } = useParams<{ id: string }>()

  const defaultAboTypes: DropDownElement[] = [
    { key: 'Off', value: t('topicsCommon:Off') },
    { key: 'Individual', value: t('topicsCommon:Individual') },
    { key: 'Automatic', value: t('topicsCommon:Automatic') },
  ]

  const dispatch = useDispatch()
  const dispatchUploadTemporaryMediaFile = useCallback((params: UploadTemporaryMediaFileRequest) => dispatch(uploadTemporaryMediaFileInitiated(params)), [dispatch])

  const uploadPending: boolean = useSelector(getUploadStatus)

  const defaultTopic: Topic = {
    name: '',
    description: '',
    defaultPostingType: AboType.Automatic,
    manualPostingAllowed: true,
    automaticPostingAllowed: true,
    active: true,
    noPostingAllowed: true,
    isInvisibleForUser: false,
  }

  const [isDirty, setIsDirty] = useState<boolean>(false)
  const [alreadySaveClicked, setAlreadySaveClicked] = useState<boolean>(false)
  const [topic, setTopic] = useState<Topic>(defaultTopic)
  const [currentTopic, setCurrentTopic] = useState<Topic | undefined>()

  useEffect(() => {
    props.aboTopics && setCurrentTopic(props.aboTopics?.topics.find((element: TopicWithMetadata) => element.topic.id === id)?.topic)
  }, [props.aboTopics])

  useEffect(() => {
    id && currentTopic ? setTopic(currentTopic) : setTopic(defaultTopic)
  }, [id, currentTopic])

  useEffect(() => {
    if (currentTopic && JSON.stringify(topic) !== JSON.stringify(currentTopic)) setIsDirty(true)
  }, [topic, id, currentTopic])

  const topicChanged = () => {
    if (topic && topic.name && topic.name !== '' && topic.description && topic.description !== '') {
      const changedData = {
        ...topic,
        id,
      }
      props.editTopic && props.editTopic(changedData)
    }
  }

  const topicCreated = () => {
    if (topic && topic.name && topic.name !== '' && topic.description && topic.description !== '') {
      props.createTopic && props.createTopic(topic)
    }
  }

  const getMaximumDefaultPostingType = (settings: Topic) => {
    let maxPostingType: AboType = AboType.Automatic
    if (!settings.automaticPostingAllowed && settings.manualPostingAllowed) {
      maxPostingType = AboType.Individual
    }
    return maxPostingType
  }

  const topicEditContent = () => {
    return (
      <>
        <Grid container item xs={12} md={props.integrateImages ? 4 : 12} spacing={2}>
          <Grid item xs={12}>
            <TextFieldComponent
              className={classes.labelInput}
              id='name'
              variant='outlined'
              size='small'
              label={t('topicsCommon:name')}
              margin='none'
              value={topic.name}
              required
              error={(!props.newTopic || (props.newTopic && alreadySaveClicked)) && (!topic.name || topic.name === '')}
              helperText={(!props.newTopic || (props.newTopic && alreadySaveClicked)) && (!topic.name || topic.name === '') && t('topicsCommon:titleRequired')}
              InputLabelProps={{
                shrink: true,
              }}
              onChange={e => {
                setTopic({
                  ...topic,
                  name: e.currentTarget.value,
                })
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFieldComponent
              className={classes.descriptionInput}
              id='desc'
              label={t('topicsCommon:description')}
              margin='none'
              rows={6}
              value={topic.description}
              required
              variant='outlined'
              error={(!props.newTopic || (props.newTopic && alreadySaveClicked)) && (!topic.description || topic.description === '')}
              helperText={(!props.newTopic || (props.newTopic && alreadySaveClicked)) && t('topicsCommon:descRequired')}
              multiline
              InputLabelProps={{
                shrink: true,
              }}
              onChange={e => {
                setTopic({
                  ...topic,
                  description: e.currentTarget.value,
                })
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack spacing={0.5} alignItems='flex-start' sx={{ mb: 1 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                color='#202020'
                control={
                  <SwitchComponent
                    checked={topic.active}
                    color='primary'
                    id='active'
                    name='active'
                    onChange={e => {
                      const active = e.target.checked
                      setTopic({
                        ...topic,
                        active,
                      })
                    }}
                  />
                }
                label={t('topicsCommon:active') as string}
              />
              <FormControlLabel
                sx={{ m: 0 }}
                color='#202020'
                control={
                  <SwitchComponent
                    checked={topic.automaticPostingAllowed}
                    color='primary'
                    id='automaticPostingAllowed'
                    name='automaticPostingAllowed'
                    onChange={e => {
                      const mandatoryTopic = e.target.checked
                      const settings = {
                        ...topic,
                        automaticPostingAllowed: mandatoryTopic,
                      }
                      setTopic({
                        ...settings,
                        defaultPostingType: getMaximumDefaultPostingType(settings),
                      })
                    }}
                  />
                }
                label={t('topicsCommon:automaticPostingAllowed') as string}
              />
              <FormControlLabel
                sx={{ m: 0 }}
                color='#202020'
                control={
                  <SwitchComponent
                    checked={(topic.automaticPostingAllowed && !topic.manualPostingAllowed && !topic.noPostingAllowed) ? false : topic.manualPostingAllowed}
                    color='primary'
                    disabled={topic.automaticPostingAllowed && !topic.manualPostingAllowed && !topic.noPostingAllowed}
                    id='manualPostingAllowed'
                    name='manualPostingAllowed'
                    onChange={e => {
                      const settings = {
                        ...topic,
                        manualPostingAllowed: e.target.checked,
                      }
                      setTopic({
                        ...settings,
                        defaultPostingType: getMaximumDefaultPostingType(settings),
                      })
                    }}
                  />
                }
                label={t('topicsCommon:manualPostingAllowed') as string}
              />
              <FormControlLabel
                sx={{ m: 0 }}
                color='#202020'
                control={
                  <SwitchComponent
                    checked={topic.isInvisibleForUser}
                    color='primary'
                    disabled={!topic.active}
                    id='isInvisibleForUser'
                    name='isInvisibleForUser'
                    onChange={e => {
                      const isInvisibleForUser = e.target.checked
                      setTopic({
                        ...topic,
                        isInvisibleForUser,
                      })
                    }}
                  />
                }
                label={t('topicsCommon:isInvisibleForUser') as string}
              />
              {props.initialValueDropdown &&
                <Box sx={{ pt: 2, width: '100%' }}>
                  <DropDownComponent
                    onChange={(elem) => {
                      setTopic({
                        ...topic,
                        defaultPostingType: elem.key as AboType,
                      })
                    }}
                    value={topic.defaultPostingType}
                    options={defaultAboTypes}
                    label={t('topicsCommon:defaultPostingType')}
                    disableClearable
                  />
                </Box>}
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.button}>
              <ButtonComponent
                text={props.newTopic ? t('topicsCommon:createTopic') : t('topicsCommon:changeTopic')}
                color='primary'
                variant='contained'
                disabled={uploadPending || (!isDirty && !props.newTopic)}
                onClick={() => {
                  setAlreadySaveClicked(true)
                  props.newTopic ? topicCreated() : topicChanged()
                }}
              />
            </Box>
          </Grid>
        </Grid>
        {props.integrateImages && (
          <Grid item xs={12} md={8} className={classes.imgBox}>
            {uploadPending
              ? <Preloader />
              : (
                <>
                  <Stack width='100%'>
                    <TopicMediaUpload
                      getMediaData={dispatchUploadTemporaryMediaFile}
                      allowVideoUpload={false}
                      file={topic.pictureUrl}
                      setMediaFile={(file: string | undefined, fileId?: string) => {
                        setTopic({
                          ...topic,
                          pictureId: fileId || undefined,
                          pictureUrl: file,
                        })
                      }}
                    />
                  </Stack>
                </>
              )}
          </Grid>
        )}
      </>
    )
  }

  return (
    <>
      {props.editMode === DisplayMode.newScreen &&
        <Paper className={classes.root}>
          <Box className={classes.button} sx={{ mb: 2 }}>
            <ButtonComponent
              text={t('topicsCommon:backToOverview')}
              color='primary'
              variant='outlined'
              onClick={() => history.goBack()}
            />
          </Box>
          <Grid container item xs={12} spacing={3}>
            {topicEditContent()}
          </Grid>
        </Paper>}
      <DialogComponent
        requestActions={false}
        title={<>{props.newTopic ? t('topicsCommon:createTopicTitle') : t('topicsCommon:changeTopicTitle')}</>}
        content={
          <Box>
            {topicEditContent()}
          </Box>
        }
        open={props.editMode === DisplayMode.popUp && props.showTopicPopUp}
        cancelLabel={null}
        confirmLabel={null}
        onClose={() => {
          props.setShowTopicPopUp(false)
          props.backToOverview && props.backToOverview()
        }}
        autoClose={false}
        disabledConfirm={uploadPending || (!isDirty && !props.newTopic)}
        closeIcon
      />
    </>
  )
}
export default TopicEdit
